<script lang="ts" setup>
import { useValidation } from "@/composables/useValidation";
import { computed, ref, watch } from "vue";
import { phoneMask } from "@/constants/mask";
import { useBaseView } from "@/composables";

const phoneLength = 11;

const model = defineModel<string | null>({ default: null });
const props = withDefaults(
  defineProps<{
    label?: string;
    required?: boolean;
  }>(),
  {
    required: false,
    label: "Phone",
  },
);

const emits = defineEmits<{
  (e: "blur"): void;
  (e: "focus"): void;
  (e: "search"): void;
}>();

const { requiredValidator, phoneValidator } = useValidation();
const { normalizePhone } = useBaseView();

const hasValueChanged = ref<boolean>(false);

const normalizedModel = computed<string | null>({
  get() {
    if (!model.value) {
      return model.value;
    }

    const digits = model.value.replace(/\D/g, "");

    if (digits.length === phoneLength && digits.startsWith("1")) {
      return digits.slice(1);
    }

    return model.value;
  },
  set(value) {
    model.value = normalizePhone(value);
  },
});

const rules = computed(() => {
  const result = [phoneValidator(model.value || "")];

  if (props.required) {
    result.push(requiredValidator(model.value || ""));
  }

  return result;
});

function onBlur() {
  if (hasValueChanged.value) {
    emits("search");
  }

  hasValueChanged.value = false;

  emits("blur");
}

watch(normalizedModel, (value, prevValue) => {
  hasValueChanged.value = value !== prevValue;
});
</script>

<template>
  <v-text-field
    v-model="normalizedModel"
    v-maska="phoneMask"
    :class="{
      required: props.required,
    }"
    :label="props.label"
    :rules="rules"
    autocomplete="off1"
    color="primary"
    density="compact"
    hide-details="auto"
    variant="outlined"
    @blur="onBlur"
    @focus="emits('focus')"
  />
</template>
